/**
 * Derive a stable {@link moduleKey} / {@link pageKey} from a tenant route pathname
 * for page-view analytics.
 *
 * Pure and side-effect-free so it is trivially unit-testable. The tenant app
 * lives under `/app`; the first segment after it identifies the module, and the
 * rest is the page. Id-like segments (Mongo ObjectIds, UUIDs, long numeric /
 * alphanumeric ids) are collapsed to `:id` so `/procurements/abc123` and
 * `/procurements/def456` roll up to the same page key.
 */

/** First pathname segment → canonical platform module key. */
const MODULE_BY_SEGMENT: Record<string, string> = {
  '': 'dashboard',
  procurement: 'procurements',
  'ai-chat': 'ai_chat',
  'project-management': 'project_management',
  website: 'website_builder',
  letterheads: 'letterheads',
  organization: 'organization',
  tools: 'tools',
  settings: 'settings',
}

/**
 * Matches id-like path segments:
 *  - 24-char hex (Mongo ObjectId)
 *  - UUID
 *  - 4+ pure digits (numeric ids)
 *  - 16+ alphanumeric chars (random ids / long slugs)
 */
const ID_LIKE =
  /^(?:[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4,}|[a-z0-9]{16,})$/i

export interface PageInfo {
  moduleKey: string
  pageKey: string
}

export function derivePageInfo(pathname: string): PageInfo {
  // Strip a leading "/app" (tenant shell); tolerate paths with or without it.
  const stripped = pathname.replace(/^\/app\/?/, '/')
  const segments = stripped.split('/').filter(Boolean)
  const moduleKey = MODULE_BY_SEGMENT[segments[0] ?? ''] ?? 'other'
  const normalized = segments.map((s) => (ID_LIKE.test(s) ? ':id' : s)).join('/')
  const pageKey = normalized ? `/${normalized}` : '/'
  return { moduleKey, pageKey }
}
